import { showToast } from "@caspeco/casper-ui-library";
import { useState } from "react";
import { openFile as appOpenFile, downloadFile as appDownloadFile } from "../utils/file-helper";
import { QueryKey, useQuery } from "@tanstack/react-query";
import { useLoadingContext } from "./use-loading-context";
import { useTranslation } from "react-i18next";
import { useAppContext } from "./use-app-context";

export const useFile = <T>({
	queryKey,
	queryFn,
}: {
	queryKey: (object?: T) => QueryKey;
	queryFn: (object: T) => () => Promise<File>;
}) => {
	const { t } = useTranslation();
	const { startLoading, stopLoading } = useLoadingContext();
	const [selectedFile, setSelectedFile] = useState<T>();
	const { safeArea } = useAppContext();

	const query = useQuery({
		queryKey: queryKey(selectedFile),
		queryFn: queryFn(selectedFile!),
		enabled: Boolean(selectedFile),
	});

	const openFile = async (object: T) => {
		return getFile(object, (file) => appOpenFile(file, file.name));
	};

	const downloadFile = async (object: T) => {
		return getFile(object, (file) => appDownloadFile(file, file.name));
	};

	const getFile = async (object: T, onSuccess: (file: File) => void) => {
		if (selectedFile) return;

		startLoading();
		try {
			setSelectedFile(object);
			const data = await query.promise;

			if (query.error || !data) {
				showToast({
					description: t("error_toast_title"),
					type: "error",
					distanceToBottom: safeArea.bottomAsPx(),
				});
			} else {
				onSuccess(data);
			}
		} catch {
			showToast({
				description: t("error_toast_title"),
				type: "error",
				distanceToBottom: safeArea.bottomAsPx(),
			});
		} finally {
			setSelectedFile(undefined);
			stopLoading();
		}
	};

	return { openFile, downloadFile, isLoading: Boolean(selectedFile) };
};
